import { lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useRouteSEO } from "./lib/seo";

const LandingPage = lazy(() => import("./components/LandingPage").then(m => ({ default: m.LandingPage })));
const DemoPage = lazy(() => import("./pages/DemoPage").then(m => ({ default: m.DemoPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then(m => ({ default: m.LoginPage })));
const ContactSalesPage = lazy(() => import("./pages/ContactSalesPage").then(m => ({ default: m.ContactSalesPage })));
const RequestAccessPage = lazy(() => import("./pages/RequestAccessPage").then(m => ({ default: m.RequestAccessPage })));
const PartnersDashboard = lazy(() => import("./pages/dashboard/PartnersDashboard").then(m => ({ default: m.PartnersDashboard })));

const WalletPage = lazy(() => import("./pages/wallet/WalletPage").then(m => ({ default: m.WalletPage })));
const StampCardPage = lazy(() => import("./pages/wallet/StampCardPage").then(m => ({ default: m.StampCardPage })));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })));

function FallbackUI() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md text-center">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Something went wrong</h1>
        <p className="mb-6 text-gray-600">
          An unexpected error occurred. Our team has been notified.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

function SEOManager() {
  useRouteSEO();
  return null;
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<FallbackUI />} showDialog>
    <BrowserRouter>
      <SEOManager />
      <Suspense fallback={null}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/contact-sales" element={<ContactSalesPage />} />
        <Route path="/request-access" element={<RequestAccessPage />} />
        {/* Customer wallet */}
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <WalletPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/wallet/:partnerUserId"
          element={
            <ProtectedRoute>
              <StampCardPage />
            </ProtectedRoute>
          }
        />

        {/* Unified dashboard — admin/moderator/partner all sign in here */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={["admin", "moderator", "partner"]}>
              <PartnersDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
